import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useCallback, useContext, useMemo } from 'react';

import { CookiePreferencesBannerContext } from '@/components/app/layout/CookiePreferencesBannerProvider';
import Layout from '@/components/layout/LayoutWrapper';
import EnCookiesMDX from '@/markdown/terms/cookies/en.mdx';
import { Locale } from '@/store/locale/types';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

interface CookiesProps {
	meta: {
		title: string;
	};
	initialState: { locale: Locale };
}

interface LocaleMdxProps {
	onOptOutClick: () => void;
}

/*
 * Need to use switch statement due to need for static strings in `import` function within `dynamic()`
 * https://nextjs.org/docs/advanced-features/dynamic-import
 * This might eventually get fixed: https://github.com/vercel/next.js/discussions/33980
 */
const localeMdx = (locale: Locale = 'en') => {
	const defaultMDX = EnCookiesMDX;

	switch (locale) {
		case 'fr':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/fr.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'ja':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/ja.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'es':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/es.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'de':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/de.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'pt':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/pt.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'ko':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/ko.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'it':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/it.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'pl':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/pl.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'zh':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/cookies/zh.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		// English fallback
		case 'en':
		default:
			return defaultMDX;
	}
};

const Cookies = ({ initialState: { locale } }: CookiesProps) => {
	const LocaleMdx = useMemo(() => localeMdx(locale), [locale]);
	const { setCanBeShown, setIsAnnounced, setIsForciblyShown } = useContext(CookiePreferencesBannerContext);
	const openCookiesBanner = useCallback(() => {
		setCanBeShown(true);
		setIsAnnounced(true);
		setIsForciblyShown(true);
	}, [setIsAnnounced, setIsForciblyShown, setCanBeShown]);

	return (
		<Layout pageName="cookies" title="Calm - Cookie Notice">
			<LocaleMdx onOptOutClick={openCookiesBanner} />
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);

	return {
		props: {
			initialState,
			hideAcceptCookiesModal: true,
		},
	};
};

export default Cookies;
